import {
  Card,
  CardBody,
  Row,
  Col,
  // Button, // Kept for potential future use
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Badge,
  Spinner,
} from 'reactstrap';
import classnames from 'classnames';
import { Icon } from '../../components/Component'; // Assuming this is a valid component
import Content from '../../layout/content/Content';
import Head from '../../layout/head/Head';
import { defaultUserImage, getPublicDocumentUrl } from '../../utils/envConfig';
import { useParams, Link } from 'react-router-dom';
import { Chart, CategoryScale, LinearScale, PointElement, LineElement, BarElement, ArcElement, Title, Tooltip, Legend, Filler } from 'chart.js';
import { useQuery } from 'react-query';
import { useMemo } from 'react';
import useAxiosPrivate from '../../hooks/useAxiosPrivate';
import { getAgentPerformance } from '../../api/agents/getAgentPerformance';
import { getAgentPerformanceHistory } from '../../api/agents/getAgentPerformanceHistory';
import { getAgentHotLeadFunnel } from '../../api/agents/getAgentHotLeadFunnel';

// Import components
import PerformanceMetrics from './components/agent-detail/PerformanceMetrics';
import PerformanceTrends from './components/agent-detail/PerformanceTrends'; // Activated
import PerformanceBreakdown from './components/agent-detail/PerformanceBreakdown';
import RecentDeals from './components/agent-detail/RecentDeals';
import RecentListings from './components/agent-detail/RecentListings';
import RecentLeadsTable from './components/agent-detail/RecentLeadsTable';
// import UpcomingTasks from './components/agent-detail/UpcomingTasks';
// import ActivityTimeline from './components/agent-detail/ActivityTimeline';
import KanbanActivityView from './components/agent-detail/KanbanActivityView';
import LeadFunnel from './components/LeadFunnel';
import LeadSourcePerformance from './components/LeadSourcePerformance';
import DateRangePresetFilter from '../components/filter-components/DateRangePresetFilter';
import useURLParams from '../../hooks/useURLParams';

// Enhanced tab styles
const tabStyles = `
  .enhanced-tabs {

    background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
    border-radius: 12px;
    // padding: 0.5rem 0.5rem 0 0.5rem;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    margin-bottom: 1rem;
  }

  .enhanced-tabs .nav-item {
    margin-right: 0.5rem;
  }

  .enhanced-tabs .nav-link {
    border: none;
    border-radius: 8px 8px 0 0;
    padding: 1rem 1.5rem;
    font-weight: 600;
    font-size: 0.9rem;
    color: #6c757d;
    background: transparent;
    transition: all 0.3s ease;
    position: relative;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    min-width: 120px;
    text-align: center;
  }

  .enhanced-tabs .nav-link:hover {
    color: #854fff;
    background: rgba(133, 79, 255, 0.1);
  }

  .enhanced-tabs .nav-link.active {
    color: #ffffff;
    background: linear-gradient(135deg, #854fff 0%, #6f42c1 100%);
    transform: translateY(-3px);
  }


  .enhanced-tabs .nav-link .tab-icon {
    margin-right: 0.5rem;
    font-size: 1rem;
    transition: transform 0.3s ease;
  }

  .enhanced-tabs .nav-link:hover .tab-icon {
    transform: scale(1.1);
  }

  .enhanced-tabs .nav-link.active .tab-icon {
    transform: scale(1.15);
    filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2));
  }



  @media (max-width: 768px) {
    .enhanced-tabs {
      padding: 0.25rem 0.5rem 0 0.5rem;
    }

    .enhanced-tabs .nav-link {
      padding: 0.75rem 1rem;
      font-size: 0.8rem;
      min-width: 100px;
    }

    .enhanced-tabs .nav-item {
      margin-right: 0.25rem;
    }

    .tab-content-wrapper {
      padding: 1rem;
    }
  }

  @media (max-width: 576px) {
    .enhanced-tabs .nav-link {
      padding: 0.5rem 0.75rem;
      font-size: 0.75rem;
      min-width: 80px;
    }

    .enhanced-tabs .nav-link .tab-icon {
      display: none;
    }
  }
`;

// Register Chart.js components
Chart.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, ArcElement, Title, Tooltip, Legend, Filler);

const AgentDetailReport = () => {
  const { id } = useParams();
  const axios = useAxiosPrivate();

  const { defaultStartDateISO, defaultEndDateISO, defaultPeriod, defaultTab } = useMemo(() => {
    const today = new Date();
    const start = new Date(today.getFullYear(), today.getMonth(), 1);
    const end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    return {
      defaultStartDateISO: start.toISOString(),
      defaultEndDateISO: end.toISOString(),
      defaultPeriod: 'monthly',
      defaultTab: '1',
    };
  }, []);

  const initialHookParams = useMemo(() => ({
    tab: defaultTab,
    period: defaultPeriod,
    startDate: defaultStartDateISO,
    endDate: defaultEndDateISO,
  }), [defaultTab, defaultPeriod, defaultStartDateISO, defaultEndDateISO]);

  const { params, updateParam } = useURLParams(initialHookParams);

  const activeTab = params.tab || defaultTab;
  const period = params.period || defaultPeriod;

  // Stable string representations of dates from URL/defaults
  const currentStartDateString = params.startDate || defaultStartDateISO;
  const currentEndDateString = params.endDate || defaultEndDateISO;

  // Memoized Date objects for components that need them (e.g., DateRangePresetFilter)
  // These are passed to DateRangePresetFilter and potentially other UI components
  const startDate = useMemo(() => (currentStartDateString ? new Date(currentStartDateString) : null), [currentStartDateString]);
  const endDate = useMemo(() => (currentEndDateString ? new Date(currentEndDateString) : null), [currentEndDateString]);


  // queryParams for API calls, now using stable strings and memoized
  const queryParams = useMemo(() => ({
    period,
    startDate: currentStartDateString,
    endDate: currentEndDateString,
  }), [period, currentStartDateString, currentEndDateString]);

  // Fetch agent performance data (current period summary)
  const {
    data: agentPerformanceData,
    isLoading: isLoadingPerformance,
  } = useQuery(
    ['agentPerformance', id, period, currentStartDateString, currentEndDateString],
    () => getAgentPerformance(axios, id, { params: queryParams }),
    {
      enabled: !!id,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
    }
  );

  // Fetch agent performance history data (for trends) - UNCOMMENTED
  const {
    data: performanceHistoryData, // Expecting API to return { data: [...] } or adjust accordingly
    isLoading: isLoadingHistory,
  } = useQuery(
    ['agentPerformanceHistory', id, period, currentStartDateString, currentEndDateString],
    () => getAgentPerformanceHistory(axios, id, { params: queryParams }),
    {
      enabled: !!id,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
    }
  );

  // Fetch agent hot lead funnel data
  const {
    data: hotLeadFunnelData,
    isLoading: isLoadingHotLeadFunnel,
  } = useQuery(
    ['agentHotLeadFunnel', id, period, currentStartDateString, currentEndDateString],
    () => getAgentHotLeadFunnel(axios, id, { params: queryParams }),
    {
      enabled: !!id,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
    }
  );

  // Use API data only
  const agentData = agentPerformanceData || {}; // Current period performance summary
  const agentInfo = agentData?.agent || {};
  const agentUser = agentInfo?.user || {};

  return (
    <>

      <Head title="Agent Detail Report" />
      <Content>
        <style>{tabStyles}</style>
        <div>
          {/* Header with back button and filters */}
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4">
            <Link to="/agent-dashboard" className="btn btn-light border mb-3 mb-md-0">
              <Icon name="arrow-left" /> Back to Leaderboard
            </Link>
            <div className="d-flex gap-2">
              <DateRangePresetFilter
                startDate={startDate}
                endDate={endDate}
                period={period}
                onDateRangeChange={(start, end) => {
                  if (start) updateParam('startDate', start.toISOString());
                  if (end) updateParam('endDate', end.toISOString());
                }}
                onPeriodChange={(newPeriod) => updateParam('period', newPeriod)}
              />
            </div>
          </div>

          {/* Agent Profile Card */}
          <Card className="shadow-sm border-0 mb-4">
            <CardBody className="p-4">
              {isLoadingPerformance ? (
                <div className="d-flex justify-content-center align-items-center py-5">
                  <Spinner color="primary" style={{ width: '3rem', height: '3rem' }} />
                </div>
              ) : (
                <Row className="align-items-center gy-3 gy-md-0">
                  <Col md="8">
                    <div className="d-flex align-items-center">
                      <div
                        className="me-3 me-md-4 flex-shrink-0"
                        style={{ width: '80px', height: '80px' }}
                      >
                        <img
                          src={getPublicDocumentUrl(agentUser?.photo) || defaultUserImage}
                          alt={`${agentUser?.first_name || agentUser?.firstName || 'Agent'} ${agentUser?.last_name || agentUser?.lastName || ''}`}
                          className="img-fluid rounded-circle"
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      </div>
                      <div>
                        <h2 className="mb-1 h3">
                          <Link
                            to={`/user-profile/${agentUser?.id}/user-profile-setting`}
                            className="text-decoration-none text-dark"
                            style={{ cursor: 'pointer' }}
                          >
                            {`${agentUser?.first_name || agentUser?.firstName || ''} ${agentUser?.last_name || agentUser?.lastName || ''}`}
                          </Link>
                        </h2>
                        <p className="mb-2 text-muted small" style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {agentInfo.agentBio || 'No bio available.'}
                        </p>
                        <div className="d-flex flex-wrap align-items-center mt-2">
                          {agentInfo.RERANo && <Badge color="primary" pill className="me-2 mb-1">RERA #{agentInfo.RERANo}</Badge>}
                          {agentInfo.languages && agentInfo.languages.map((lang, idx) => (
                            <Badge color="secondary" pill key={idx} className="me-2 mb-1 fw-normal">{lang}</Badge>
                          ))}
                          {agentUser.email && <span className="text-muted me-3 mb-1 small"><Icon name="mail" className="me-1" />{agentUser.email}</span>}
                          {agentUser.phone && <span className="text-muted mb-1 small"><Icon name="phone" className="me-1" />{agentUser.phone}</span>}
                        </div>
                      </div>
                    </div>
                  </Col>
                  <Col md="4" className="text-md-end">
                    <div className="text-center p-3 bg-light rounded shadow-sm">
                      <h6 className="mb-1 text-muted fw-bold">Overall Ranking</h6>
                      <h1 className="display-5 mb-0 text-primary">{agentData.rank || 'N/A'}</h1>
                      <p className="text-muted small mb-0">of {agentData.agentsCount || '...'} agents</p>
                    </div>
                  </Col>
                </Row>
              )}
            </CardBody>
          </Card>

          {/* Performance Metrics (Existing) */}
          <PerformanceMetrics data={agentData} isLoading={isLoadingPerformance} agentPerformance={agentData} // Current period summary from agentPerformanceData
            hotLeadFunnel={hotLeadFunnelData} // Funnel summary from hotLeadFunnelData
          />



          {/* Enhanced Tabs for different reports */}
          <Nav tabs className="enhanced-tabs">
            <NavItem>
              <NavLink
                className={classnames('fw-medium', { active: activeTab === '1' })}
                onClick={() => updateParam('tab', '1')}
                href="#"
              >
                {/* <Icon name="bar-chart" className="tab-icon" /> */}
                Performance
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={classnames('fw-medium', { active: activeTab === '2' })}
                onClick={() => updateParam('tab', '2')}
                href="#"
              >
                {/* <Icon name="handshake" className="tab-icon" /> */}
                Deals & Listings
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={classnames('fw-medium', { active: activeTab === '3' })}
                onClick={() => updateParam('tab', '3')}
                href="#"
              >
                {/* <Icon name="calendar-alt" className="tab-icon" /> */}
                Tasks & Activities
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink
                className={classnames('fw-medium d-flex align-items-center justify-content-center', { active: activeTab === '4' })}
                onClick={() => updateParam('tab', '4')}
                href="#"
              >
                {/* <Icon name="users" className="tab-icon" /> */}
                Leads
              </NavLink>
            </NavItem>
          </Nav>

          <div className="tab-content-wrapper">
            <TabContent activeTab={activeTab}>
              <TabPane tabId="1">
                {/* Performance Tab */}


                <Row className="g-4 mb-4">
                  <Col lg="6">
                    <PerformanceBreakdown
                      data={agentData} // Current period summary
                      isLoading={isLoadingPerformance}
                    />
                  </Col>
                  <Col lg="6">
                    {isLoadingHotLeadFunnel ? (
                      <Card className="shadow-sm border-0">
                        <CardBody className="p-4 d-flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
                          <Spinner color="primary" />
                        </CardBody>
                      </Card>
                    ) : (
                      <LeadFunnel
                        data={hotLeadFunnelData || {}} // Pass the whole hotLeadFunnelData for calculations
                        isHotLeadData={true}
                        // showPercentages={true} // Retained, but stage-by-stage is more specific
                        title="Lead Conversion Funnel (Hot Leads)"
                        showStageByStageRates={true} // New prop for enhanced display
                      />
                    )}
                  </Col>
                </Row>

                <Row className="g-4 mb-4">
                  <Col lg="12">
                    {isLoadingHotLeadFunnel ? (
                      <Card className="shadow-sm border-0">
                        <CardBody className="p-4 d-flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
                          <Spinner color="primary" />
                        </CardBody>
                      </Card>
                    ) : (
                      <LeadSourcePerformance
                        data={hotLeadFunnelData?.hotLeadSourceMetrics || {}}
                        title="Lead Source Performance (Hot Leads)"
                        enableCharts={true} // New prop to enable graphical display
                        isLoading={isLoadingHotLeadFunnel} // Pass isLoading for internal handling in child
                      />
                    )}
                  </Col>
                </Row>
                <Row className="g-4 mb-4">
                  <Col lg="12">
                    <PerformanceTrends
                      historyData={performanceHistoryData?.data || []} // Ensure your API returns { data: [...] } or adjust access
                      isLoading={isLoadingHistory}
                      period={period}
                    />
                  </Col>
                </Row>
              </TabPane>

              <TabPane tabId="2">
                <Row className="g-4">
                  <Col lg="12">
                    <RecentDeals agentId={id}
                      limit={5}
                      viewAllLink={`/list-deals?category=%5B"Primary%20Off%20Plan%20Property"%2C"Secondary%20Market%20Property"%5D&agent=${id}`} />
                  </Col>
                </Row>
                <Row className="mt-4"> {/* Added margin-top for spacing */}
                  <Col lg="12">
                    <RecentListings agentId={id}
                      limit={5}
                      viewAllLink={`/list-deals?category=%5B"Listing%20Acquired"%5D&agent=${id}`} />
                  </Col>
                </Row>
              </TabPane>

              <TabPane tabId="3">
                <Row className="g-4 mt-0">
                  <Col lg="12">
                    <KanbanActivityView userId={agentUser?.id} /> {/* Assuming agentUser.id is correct */}
                  </Col>
                </Row>
              </TabPane>

              <TabPane tabId="4">
                <Row className="g-4">
                  <Col lg="12">
                    <RecentLeadsTable agentId={id} />
                  </Col>
                </Row>
              </TabPane>
            </TabContent>
          </div>
        </div>
      </Content>
    </>
  );
};

export default AgentDetailReport;
